import React, { useEffect, useRef, useState } from "react";
import { Feather, X } from "lucide-react";
import { consumeCarryingAffirmation } from "@/lib/carryingAffirmation";

/**
 * CarryingAffirmationPanel — a TEMPORARY contextual section that appears
 * above the Journal composer when the member arrives via "Carry into
 * Journal" from a Rituals or Home affirmation.
 *
 * Behaviour:
 *   • Reads the carrying affirmation once on mount via
 *     `consumeCarryingAffirmation()` (which also clears the key), so a
 *     plain visit to Journal never renders this panel.
 *   • Shows "TODAY I'M CARRYING" + the affirmation + the optional
 *     reflection prompt.
 *   • Can be dismissed with ×. Does not pre-fill the composer.
 *   • Does not persist. On reload or next Journal visit the panel is gone.
 *
 * The composer itself is untouched — this panel is purely contextual.
 */
export default function CarryingAffirmationPanel() {
  const [carrying, setCarrying] = useState(null);
  // StrictMode double-invokes effects in dev; without a guard the second
  // run would consume a now-empty key and reset `carrying` back to null.
  const consumedRef = useRef(false);

  useEffect(() => {
    if (consumedRef.current) return;
    consumedRef.current = true;
    setCarrying(consumeCarryingAffirmation());
  }, []);

  if (!carrying) return null;

  return (
    <section
      className="relative overflow-hidden rounded-md border hairline bg-gradient-to-br from-[hsl(var(--sand))]/50 via-[hsl(var(--paper))] to-[hsl(var(--paper))] p-6 md:p-8 reveal"
      data-testid="journal-carrying-affirmation"
      aria-live="polite"
    >
      <button
        type="button"
        onClick={() => setCarrying(null)}
        data-testid="journal-carrying-dismiss"
        aria-label="Dismiss"
        className="absolute top-3 right-3 w-7 h-7 grid place-items-center rounded-full text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--fg))] hover:bg-[hsl(var(--sand))]/60 tab-link"
      >
        <X size={13} />
      </button>

      <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[hsl(var(--terracotta))]">
        <Feather size={11} />
        <span data-testid="journal-carrying-eyebrow">Today I&apos;m carrying</span>
        {carrying.category && (
          <span className="normal-case tracking-normal text-[hsl(var(--muted-foreground))]">
            · {carrying.category}
          </span>
        )}
      </div>

      <p
        className="mt-4 font-display italic text-xl md:text-2xl leading-snug text-[hsl(var(--fg))]"
        data-testid="journal-carrying-text"
      >
        &ldquo;{carrying.text}&rdquo;
      </p>

      <p
        className="mt-4 text-[13px] text-[hsl(var(--muted-foreground))] italic"
        data-testid="journal-carrying-prompt"
      >
        What does living from these words look like for me today?
      </p>
    </section>
  );
}
